import { Component, OnDestroy, OnInit } from '@angular/core';
import { DialogService, FileService, ReportService, SnackService } from '@shared/services';
import { AdminService } from '../../../../admin.service';
import { IReport, ReportStatus } from '@shared/interfaces';
import { AdminActionsDirective } from '@app/main/admin/directives/admin-actions.directive';
import { Subscription } from 'rxjs';
import { AdminReportService } from '@app/main/admin/modules/admin-report/admin-report-service';
import { TranslateService } from '@ngx-translate/core';
import { ModuleService } from '@shared/services/module.service';

@Component({
    selector: 'app-admin-report-list',
    templateUrl: './admin-report-list.component.html',
    styleUrls: ['./admin-report-list.component.scss'],
    standalone: false
})
export class AdminReportListComponent extends AdminActionsDirective implements OnInit, OnDestroy {
    subscriptions: Subscription[] = [];
    currentYear = new Date().getFullYear();

    constructor(
        private adminReportService: AdminReportService,
        private reportService: ReportService,
        private snackService: SnackService,
        private fileService: FileService,
        private translate: TranslateService,
        adminService: AdminService,
        dialogService: DialogService,
        moduleService: ModuleService
    ) {
        super(adminService, dialogService, moduleService);
    }

    get currentItem(): IReport {
        return this.adminReportService.currentItem;
    }

    get filteredChildren(): IReport[] {
        return this.currentItem.children.filter(e => !e.filtered);
    }

    get lastActiveItem(): IReport {
        return this.adminReportService.lastActiveItem;
    }

    get historyStack(): IReport[] {
        return this.adminReportService.historyStack;
    }

    get filteredHistoryStack(): IReport[] {
        return this.adminReportService.historyStack?.filter(e => e?.title);
    }

    get headline(): string {
        return this.filteredHistoryStack?.length
            ? [...this.filteredHistoryStack, this.currentItem].map(e => this.getItemTitle(e.title)).join(' - ')
            : this.getItemTitle(this.currentItem?.title);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.adminService.selectedItemChanged.subscribe(e => {
                if (e) {
                    this.adminReportService.currentItem = e;
                }
            }),
            this.adminService.dataChanged.subscribe(children => {
                if (this.lastActiveItem) {
                    this.adminReportService.replaceHistory(children);

                    const item = this.adminReportService.getItem(children, this.lastActiveItem.id);
                    if (item) {
                        this.adminReportService.currentItem = this.selectedItem = item;
                    } else {
                        this.adminReportService.lastActiveItem = null;
                        this.adminReportService.currentItem = { children } as IReport;
                        this.selectedItem = null;
                    }
                } else if (!this.selectedItem?.id) {
                    this.adminReportService.currentItem = { children };
                } else {
                    this.adminReportService.replaceHistory(children);
                    this.adminReportService.currentItem = this.selectedItem = this.adminReportService.getItem(
                        children,
                        this.selectedItem.id
                    );
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(e => e.unsubscribe());
    }

    selectItem(item: IReport) {
        if ((item.children || !item.modifiedAt) && !item.endNode) {
            this.adminReportService.selectItem(item);
            this.selectedItem = item;
        }
    }

    goBackInList() {
        this.selectedItem = this.adminReportService.goBack();
    }

    async exportItem(item: IReport) {
        await this.fileService.exportReport(item);
    }

    async deleteReport(item: IReport): Promise<void> {
        try {
            const result = await this.deleteDialog();
            if (!result) {
                return;
            }
        } catch (e) {
            throw new Error('Failed to confirm report deletion.', { cause: e });
        }

        try {
            await this.reportService.deleteItem(item);
            this.snackService.openSnackBarSuccess(`Report item ${item.id} deleted successfully.`);

            this.goToReports();
            if (this.currentItem.children.length <= 1) {
                if (this.historyStack[this.historyStack.length - 1].children.length <= 1) {
                    this.adminReportService.goToFirstView();
                } else {
                    this.goBackInList();
                }
            }
        } catch (e) {
            this.snackService.openSnackBarError(e.error.message);
        }
    }

    async resetReport(item: IReport): Promise<void> {
        try {
            const result = await this.resetDialog();
            if (!result) {
                return;
            }
        } catch (e) {
            throw new Error('Failed to confirm report reset.', { cause: e });
        }

        try {
            await this.reportService.resetItem({ id: item.id });
            this.snackService.openSnackBarSuccess(`Report item ${item.id} reset successfully.`);
            this.goToReports();
        } catch (e) {
            this.snackService.openSnackBarError(e.error.message);
        }
    }

    cannotBeReverted(item: IReport): boolean {
        return new Date(item.dueDate).getFullYear() !== this.currentYear;
    }

    getItemTitle(title: string): string {
        return title === 'rc' || title === 'sag' ? this.translate.instant(`responsible.item.types.${title}`) : title;
    }

    isFinished(item: IReport): boolean {
        return this.cannotBeReverted(item) && item.reportStatus === ReportStatus.released;
    }
}
